/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useId } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { cn } from '@/lib/utils';
import { NM_SORT_OPTIONS, resolveSortOptions, selectSortOption } from '@/lib/search/sort-options';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

/**
 * NM SRP sort control (search-3a-comparison.md P1, demo-critical; blast-4
 * "Sort row"): a NATIVE <select> — live NM uses one — with the "Sort By:"
 * label on its left. Box metrics/border/chevron come from the .srp-sort-select
 * rules (theme/overrides/search.css) reading the --srp-sort-* tokens
 * (tokens/search.css): 145x28 box, 13px type, bottom-only 1px border
 * rgb(139,139,139), custom chevron.
 *
 * URL-driven (?sort=<id> through the search loader's existing SCAPI `sort`
 * pass-through) with the pagination params reset. Options resolve via
 * resolveSortOptions: server sortingOptions when the sandbox has them,
 * the curated live-NM five otherwise (see lib/search/sort-options.ts for the
 * sandbox-gap note — the wiring is real, the sandbox data is not there yet).
 */
export default function SortSelect({
    serverOptions,
    className,
}: {
    serverOptions?: Array<{ id?: string; label?: string }>;
    className?: string;
}): ReactElement {
    const { t } = useTranslation('search');
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const selectId = useId();
    const isPending = navigation.state !== 'idle';

    // NM's list is named explicitly rather than taken as a default: this
    // control is NM-gated (search-ux.ts `nmPagers`), and BG's SRP mounts
    // `SortMenu` with BG's own list instead.
    const options = resolveSortOptions(serverOptions, NM_SORT_OPTIONS);

    // Optimistic: read the in-flight target's sort while a navigation pends.
    const urlSort = navigation.location
        ? new URLSearchParams(navigation.location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT)
        : new URLSearchParams(location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT);

    // `selectSortOption`, NOT the raw param and NOT `options[0]`. Two ways the
    // lookup comes back empty, and both have to land on the same answer:
    //
    //   - no `?sort=` at all. NM's live order puts Best Match LAST while it
    //     remains the selected default, so reading position 0 made the control
    //     display `price-high-to-low` — a sort the loader never applied.
    //   - a `?sort=` the list does not contain. Passing that straight into a
    //     controlled <select> leaves `value` matching no <option>, so the
    //     control misnames the ordering (see this file's test for what each
    //     environment paints).
    //
    // `selectSortOption` resolves both through `defaultSortId`, which prefers a
    // row marked `isDefault` and otherwise takes the first entry — so the
    // server-configured path, whose options carry no `isDefault`, is unchanged.
    // The helper is shared with BG's `SortMenu` rather than duplicated: the
    // brands' lists are no longer the same set, and BG is where the
    // unrecognized-id case is reachable by an ordinary cross-brand link.
    const currentSort = selectSortOption(options, urlSort ?? undefined)?.id;

    const onChange = useCallback(
        (sort: string) => {
            const params = new URLSearchParams(location.search);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sort);
            // Reset paging — both the legacy offset and the crawlable page param.
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
            params.delete('page');
            void navigate({ ...location, search: `?${params.toString()}` });
        },
        [location, navigate]
    );

    return (
        <div
            className={cn(
                'flex items-center gap-2',
                isPending && 'pointer-events-none opacity-50 transition-opacity',
                className
            )}>
            <label htmlFor={selectId} className="srp-sort-label whitespace-nowrap">
                {t('sort.label', { defaultValue: 'Sort By:' })}
            </label>
            <select
                id={selectId}
                data-testid="search-sort-select"
                className="srp-sort-select"
                value={currentSort}
                onChange={(e) => onChange(e.target.value)}>
                {options.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.label ??
                            (option.labelKey
                                ? t(option.labelKey, { defaultValue: option.defaultLabel ?? option.id })
                                : option.id)}
                    </option>
                ))}
            </select>
        </div>
    );
}
